import React from 'react';
import {
    StartView,
    StartViewEnterType,
} from '@src/slide/application/service/slide-rating-and-improve/view/mobile/start/start';
import {
    SlideContentAnalyzer,
} from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import LayoutHeader from '@com-m/pull-panel-m/layout-header';
import i18n from '@i18next/i18n';
import { TitleAlignmentPreview }
    from '@src/slide/application/service/slide-rating-and-improve/view/mobile/result/title-alignment-preview/title-alignment-preview';
import {
    IShowContentChangeOption,
} from '@src/slide/application/service/slide-rating-and-improve/content-layout/result/title-alignment-result';
import {
    CONTENT_MARGIN_BOTTOM,
    CONTENT_MARGIN_TOP, PAGE_NUM_AND_BUTTON,
    PANEL_HEADER_HEIGHT, SAFE_AREA_BOTTOM, START_AND_RESULT_HEIGHT,
    TAB_HEIGHT,
} from '@src/slide/application/service/slide-rating-and-improve/view/mobile/result/constant';
import isIosSafeBottomDevice from '@utils/safe-area';
import PullPanel from '@tencent/dui-mobile/lib/components/PullPanel';
import {
    IMobileRatingService,
} from '@src/slide/application/service/slide-rating-and-improve/mobile-slide-rating-service.interface';
import { ResultMode } from '@src/slide/application/service/slide-rating-and-improve/view/mobile/result/rating-result';
import { reaction } from 'mobx';
import { MobileUIState } from '@src/slide/infrastructure/module/independent/mobile/mobile-ui-state';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

export enum PullPanelContentType {
    StartAndResult = 0,
    TitleAlignment = 1,
}

/**
 * 在预览页展示还是在编辑页展示
 */
export enum PullPanelScene {
    Preview = 0,
    Edit = 1,
}

export interface PullPanelContentProps {
    scene: PullPanelScene,
    mobileRatingService: IMobileRatingService;
    analyzer: SlideContentAnalyzer,
    selectionService?: ISelectionService,
    handleOnClose?: () => void,
    handleToOptimize?: () => void;
    getPanelFunc?: () => any;
}

interface PullPanelContentState {
    viewType: PullPanelContentType;
}

export class PullPanelContent extends React.Component<PullPanelContentProps, PullPanelContentState> {
    private titleAlignmentInfo: {
        options: IShowContentChangeOption,
        contentSize: { width: number, height: number; },
    };
    private enterType: StartViewEnterType;

    public constructor(props: PullPanelContentProps) {
        super(props);
        this.state = {
            viewType: PullPanelContentType.StartAndResult,
        };
        this.enterType = this.props.mobileRatingService.isWaitingForShowInEdit
            ? StartViewEnterType.Result : StartViewEnterType.Guide;
        this.props.analyzer.setShowContentChangeUI(this.showContentChangeUI);
    }

    public render() {
        const resultMode = this.props.scene === PullPanelScene.Preview ? ResultMode.Preview : ResultMode.Edit;
        return (
            <>
                <StartView
                    enterType={this.enterType}
                    resultMode={resultMode}
                    hidden={this.state.viewType !== PullPanelContentType.StartAndResult}
                    analyzer={this.props.analyzer}
                    handleToOptimize={this.props.handleToOptimize}
                    selectionService={this.props.selectionService}
                ></StartView>
                {
                    this.state.viewType === PullPanelContentType.TitleAlignment
                    && <TitleAlignmentPreview
                        options={this.titleAlignmentInfo.options}
                        contentSize={this.titleAlignmentInfo.contentSize}
                        onConfirm={this.onConfirmChangeTitle.bind(this, this.titleAlignmentInfo.options)}
                    />
                }
            </>
        );
    }

    public componentDidMount() {
        if (!this.props.mobileRatingService.isWaitingForShowInEdit) {
            // 从预览跳转到编辑的算曝光一次
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.AUTO_BEAUTIFY,
                action: TDWActionType.BEAUTIFY_FUNCTION_VIEW,
            });
        }
        // 已经完成从预览页到编辑页的切换，改回字段的默认值
        if (this.props.mobileRatingService.isWaitingForShowInEdit) {
            this.props.mobileRatingService.isWaitingForShowInEdit = false;
        }
    }

    /**
     * 标题对齐使用的页面对比的UI
     * @param options
     */
    private showContentChangeUI = (options: IShowContentChangeOption) => {
        const contentSize = this.calculateContentSize(options);
        const panelHeight = window.screen.height / 2;
        const panel = this.props.getPanelFunc?.();
        this.titleAlignmentInfo = {
            options,
            contentSize,
        };
        if (panel) {
            panel?.update({
                panelHeight: contentSize.panelHeight,
                showHeaderBorder: false,
                header: <LayoutHeader
                    showBack={true}
                    onBack={() => {
                        this.backToResultUI();
                    }}
                    onClose={this.props.handleOnClose || (() => {}) }
                    title={i18n.t('标题对齐预览')}
                />,
            });
            this.setState({ viewType: PullPanelContentType.TitleAlignment });
        } else {
            const titlePanel = PullPanel.show({
                pullable: false,
                panelHeight,
                headerStyle: { height: `${PANEL_HEADER_HEIGHT}px`, padding: '0 0' },
                showHeaderBorder: false,
                header: <LayoutHeader
                    onClose={() => {
                        titlePanel?.close();
                    }}
                    title={i18n.t('排版检查和建议')}>
                </LayoutHeader>,
                content: <TitleAlignmentPreview
                    options={this.titleAlignmentInfo.options}
                    contentSize={this.titleAlignmentInfo.contentSize}
                    onConfirm={() => {
                        titlePanel?.close();
                        this.titleAlignmentInfo.options.onConfirmModify?.();
                    }}
                />,
            });
            const editStateDisposer = reaction(() => MobileUIState.isEditing, (isEditing) => {
                if (!isEditing) {
                    titlePanel?.close();
                    editStateDisposer?.();
                }
            });
            window.addEventListener('orientationchange', () => {
                titlePanel?.close();
            });
        }
    };

    private backToResultUI() {
        const panelHeight = window.screen.height / 2;
        const panel = this.props.getPanelFunc?.();
        panel?.update({
            panelHeight,
            showHeaderBorder: true,
            header: <LayoutHeader
                onClose={this.props.handleOnClose || (() => {}) }
                title={i18n.t('排版检查和建议')}>
            </LayoutHeader>,
        });
        this.setState({ viewType: PullPanelContentType.StartAndResult });
    }

    private calculateContentSize(options: IShowContentChangeOption) {
        const noChangeHeight = PANEL_HEADER_HEIGHT + TAB_HEIGHT
            + CONTENT_MARGIN_TOP + CONTENT_MARGIN_BOTTOM + PAGE_NUM_AND_BUTTON;
        const safeBottomHeight = isIosSafeBottomDevice() ? SAFE_AREA_BOTTOM : 0;
        const contentMaxHeight = window.innerHeight - 24 - noChangeHeight - safeBottomHeight;
        const contentMaxWidth = Math.ceil(window.innerWidth - 56);
        const contentHeight = Math.ceil(contentMaxWidth / options.slideWidth * options.slideHeight);
        if (contentHeight <= contentMaxHeight) {
            return {
                width: contentMaxWidth,
                height: contentHeight,
                panelHeight: noChangeHeight + safeBottomHeight + contentHeight,
            };
        }
        return {
            width: contentMaxWidth,
            height: contentMaxHeight,
            panelHeight: noChangeHeight + safeBottomHeight + contentMaxHeight,
        };
    }


    private onConfirmChangeTitle = (options: IShowContentChangeOption) => {
        options.onConfirmModify?.();
        this.backToResultUI();
    };
}
